import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getPrivateMoneys } from 'admin/actions/private-moneys';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import Headings from 'admin/components/atoms/Headings';
import Select from 'admin/components/atoms/Select';
import TextBox from 'admin/components/atoms/TextBox';
import TextArea from 'admin/components/atoms/TextArea';
import { Button } from 'admin/components/atoms/Buttons';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import Input from 'admin/components/shared/Input';
import Img from 'admin/components/atoms/Img';
import ToggleSwitch from 'admin/components/atoms/ToggleSwitch';
import useOAuthClient from 'admin/hooks/useOAuthClient';
import ApiResults from 'admin/components/molecules/ApiResults';
import { OAuthClientHeaders, Row, RowCell } from './OAuthClientHeaders';
import CheckBoxList from './CheckBoxList';
import OAuthClientConfirmModal from './OAuthClientConfirmModal';
import { useOAuthClientForm } from './useOAuthClientForm';
import {
  allowedGrantTypeOptions,
  defaultScopeOptions,
  creatingAccountTypeOptions,
  concealUserExistenceOptions,
} from './constants';

const FormWrapper = styled.div`
  ${TextBox} {
    width: 500px;
  }
  ${Select} {
    width: 500px;
  }
  ${TextArea} {
    width: 500px;
    resize: both;
  }
`;

const OAuthClientDetail = ({ match, user }) => {
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: 'サービス管理', href: '', isCurrentPage: true },
    { value: 'OAuthClient', href: '/service-settings/oauth-clients', isCurrentPage: false },
    { value: 'OAuthClient詳細', href: '', isCurrentPage: true },
  ];

  const oauthClientId = match.params.id;
  const { oauthClient, submitUpdate } = useOAuthClient(oauthClientId);
  const { form, setForm, handleChangeForm, imgPreview } = useOAuthClientForm(oauthClient);

  const [defaultMoneyOptions, setDefaultMoneyOptions] = useState([{ label: '指定しない', value: null }]);
  const [updateClientSecret, setUpdateClientSecret] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiResult, setApiResult] = useState(null);

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onSuccess = () => {
    setApiResult({ text: 'OAuthClientは正常に更新されました', isError: false });
  };

  const registerOAuthClient = () => {
    let bodyParams = {
      name: form.name,
      redirect_uri: form.redirect_uri,
      creating_account_type: form.creating_account_type,
      description: form.description,
      allowed_grant_types: form.allowed_grant_types.join(' '),
      default_scopes: form.default_scopes.join(' '),
      default_private_money_id: form.default_private_money !== null ? form.default_private_money.id : null,
    };

    if (typeof form.oauth_client_image !== 'string') {
      bodyParams = Object.assign({}, bodyParams, { image: form.oauth_client_image });
    }

    if (form.conceal_user_existence !== null) {
      bodyParams = Object.assign({}, bodyParams, { conceal_user_existence: form.conceal_user_existence });
    }

    if (updateClientSecret === true) {
      bodyParams = Object.assign({}, bodyParams, { update_client_secret: 'true' });
      setUpdateClientSecret(false);
    }

    submitUpdate(form.id, bodyParams, onSuccess);

    closeModal();
  };

  useEffect(() => {
    if (form.organization != null) {
      getPrivateMoneys(`organization_code=${form.organization.code}&per_page=10000`).then((ret) => {
        const moneys = ret.rows.map(m => ({ label: m.name, value: m }));
        setDefaultMoneyOptions(moneys.concat({ label: '指定しない', value: null }));
      });
    }
  }, [form.organization]);

  useEffect(() => {
    if (form.creating_account_type.toLowerCase() !== 'one') {
      setForm({ ...form, default_private_money: null });
    }
  }, [form.creating_account_type]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs} />
      <Headings.h5>OAuthClient詳細</Headings.h5>
      <form onSubmit={openModal} >
        <Row>
          <OAuthClientHeaders keyName="id" />
          <RowCell>{form.id}</RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="name" />
          <RowCell>
            <TextBox
              value={form.name}
              onBlur={handleChangeForm('name', v => v.target.value)}
              required
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="client_secret" />
          <RowCell>
            {form.client_secret}
            <div>
              <ToggleSwitch
                checkedText="更新"
                uncheckedText="更新"
                height={25}
                width={60}
                checked={updateClientSecret}
                onChange={setUpdateClientSecret}
              />
            </div>
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="redirect_uri" />
          <RowCell>
            <TextBox
              value={form.redirect_uri}
              onBlur={handleChangeForm('redirect_uri', v => v.target.value)}
              required
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="allowed_grant_types" />
          <RowCell>
            <CheckBoxList
              name="allowed-grant-types"
              options={allowedGrantTypeOptions}
              values={form.allowed_grant_types}
              onChange={handleChangeForm('allowed_grant_types')}
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="default_scopes" />
          <RowCell>
            <CheckBoxList
              name="default-scopes"
              options={defaultScopeOptions}
              values={form.default_scopes}
              onChange={handleChangeForm('default_scopes')}
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="organization" />
          <RowCell>{form.organization !== null ? form.organization.name : null}</RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="creating_account_type" />
          <RowCell>
            <Select
              options={creatingAccountTypeOptions}
              value={
                creatingAccountTypeOptions.find(v => (
                  v.value === form.creating_account_type.toLowerCase()
                ))
              }
              onChange={handleChangeForm('creating_account_type', v => v.value)}
            />
          </RowCell>
        </Row>
        {form.creating_account_type.toLowerCase() === 'one'
         ? (
           <Row>
             <OAuthClientHeaders keyName="default_private_money" />
             <RowCell>
               <Select
                 Select
                 options={defaultMoneyOptions}
                 value={
                   form.default_private_money !== null
                     ? { label: form.default_private_money.name, value: form.default_private_money }
                   : { label: '指定しない', value: null }
                 }
                 onChange={handleChangeForm('default_private_money', v => v.value)}
               />
             </RowCell>
           </Row>
         )
         : null}
        <Row>
          <OAuthClientHeaders keyName="description" />
          <RowCell>
            <TextArea
              cols="90"
              value={form.description || ''}
              onChange={handleChangeForm('description', v => v.currentTarget.value)}
              maxLength={300}
              placeholder="OAuthClient説明文を記載"
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="oauth_client_image" />
          <RowCell>
            <Img
              src={imgPreview !== null ? imgPreview : form.oauth_client_image}
              alt=""
              height="100px"
              style={{ padding: '10px' }}
            />
            <Input
              type="file"
              onChange={handleChangeForm('oauth_client_image', v => v.target.files[0])}
              accept="image/jpeg, image/png"
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="conceal_user_existence" />
          <RowCell>
            <Select
              options={concealUserExistenceOptions}
              value={concealUserExistenceOptions.find(op => op.value === form.conceal_user_existence)}
              onChange={handleChangeForm('conceal_user_existence', v => v.value)}
            />
          </RowCell>
        </Row>
        <Button>更新</Button>
        {apiResult ? <ApiResults messages={[apiResult]} /> : null}
      </form>
      <ConfirmModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        title="更新内容の確認"
        form={(
          <OAuthClientConfirmModal
            form={form}
            onConfirm={registerOAuthClient}
            onCancel={closeModal}
            imgPreview={imgPreview}
            updateClientSecret={updateClientSecret}
          />
        )}
      />
    </FormWrapper>
  );
};

OAuthClientDetail.propTypes = {
  user: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  user: state.user,
});

export default connect(mapStateToProps)(OAuthClientDetail);
